import { Component, ElementRef, inject, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { SearchInputComponent } from "../../../helper/search-input/search-input.component";
import { MatActionList, MatListModule } from '@angular/material/list';
import { Account, SubscriptionTier, tiers } from '../../../service/account.service';
import { AdminService } from '../../../service/admin.service';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { EditUserComponent } from './dialog/edit-user/edit-user.component';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { AvatarComponent } from '../../../helper/avatar/avatar.component';

@Component({
  selector: 'app-users',
  imports: [
    SearchInputComponent,
    MatListModule,
    MatPaginatorModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatTooltipModule,
    RouterLink,
    AvatarComponent
  ],
  templateUrl: './users.component.html',
  styleUrl: './users.component.less'
})
export class UsersComponent implements AfterViewInit, OnDestroy {
  adminService = inject(AdminService);
  dialog = inject(MatDialog);

  @ViewChild('loadMoreButton') loadMoreButton!: ElementRef<HTMLButtonElement>;

  users: Account[] = [];
  search: string = "";
  pageSize: number = 10;
  pageIndex: number = 1;
  isLoading: boolean = false;
  hasMoreData: boolean = true;
  private intersectionObserver?: IntersectionObserver;
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor() {
    // Set up debounced search
    this.searchSubject.pipe(
      debounceTime(500), // 500ms delay for auto-search
      takeUntil(this.destroy$)
    ).subscribe((searchTerm: string) => {
      this.search = searchTerm;
      this.pageIndex = 1;
      this.hasMoreData = true;
      this.fetchUsers(true);
    });
  }

  onSearch(search: string) {
    this.search = search;
    this.pageIndex = 1;
    this.hasMoreData = true;
    this.fetchUsers(true);
  }

  onPageChange(event: PageEvent) {
    console.log("page changed to ", event.pageIndex, " with size ", event.pageSize);
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.fetchUsers(true);
  }


  fetchUsers(reset: boolean = false) {
    if (this.isLoading || (!this.hasMoreData && !reset)) return;
    
    this.isLoading = true;
    
    this.adminService.fetchUsers(this.search, this.pageIndex, this.pageSize).subscribe((newUsers) => {
      if (reset) {
        this.users = newUsers;
      } else {
        this.users = [...this.users, ...newUsers];
      }
      this.isLoading = false;

      if (newUsers.length < this.pageSize) {
        this.hasMoreData = false;
      }

      this.pageIndex++;
    });
  }

  @ViewChild('search') searchComponent!: SearchInputComponent;

  closeSearchView() {
    this.searchComponent.closeSearchView();
  }

  openUser(user: Account) {
    const editDialog = this.dialog.open(EditUserComponent, {
      data: {
        user: user
      }
    });

    // Listen for user updates from the edit dialog
    editDialog.componentInstance.userUpdated.subscribe((updatedUser: Account) => {
      // Update the user in the local array
      const index = this.users.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        this.users[index] = updatedUser;
      }
    });
  }

  onInputChange(input: string) {
    this.searchSubject.next(input);
  }

  loadMore() {
    if (!this.isLoading && this.hasMoreData) {
      this.fetchUsers();
    }
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
  }

  ngOnDestroy() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          console.log(entry)
          if (entry.isIntersecting && this.hasMoreData && !this.isLoading) {
            this.loadMore();
          }
        });
      },
      {
        root: null,
        rootMargin: '100px', // Start loading when button is 100px away from viewport
        threshold: 0.1
      }
    );

    // Start observing the load more button
    if (this.loadMoreButton) {
      console.log(this.loadMoreButton)
      this.intersectionObserver.observe(this.loadMoreButton.nativeElement);
    }
  }

  ngOnInit() {
    this.fetchUsers(true);
  }

  getUserSubscriptionTier(user: Account): SubscriptionTier {
    let tier = tiers[-1];
    if (user.connections.patreon) {
      tier = user.connections.patreon.subscriptionTier;
    }
    if (tier === undefined) {
      tier = tiers[-1];
    }
    return tier;
  }
}
